'use client';

import { useState } from 'react';
import AdvancedIndicatorsChart from '@/components/AdvancedIndicatorsChart';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdvancedIndicatorsPage() {
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>(['RSI', 'MACD', 'BB', 'SMA', 'EMA']);

  const availableIndicators = [
    'RSI', 'MACD', 'BB', 'SMA', 'EMA', 'STOCH', 'ATR', 'ADX', 'CCI', 'WILLIAMS_R', 'OBV', 'VWAP'
  ];

  const popularSymbols = [
    'AAPL', 'TSLA', 'GOOGL', 'MSFT', 'AMZN', 'META', 'NVDA', 'SPY', 'QQQ',
    'BTCUSDT', 'ETHUSDT', 'BNBUSDT',
    'EUR_USD', 'GBP_USD', 'USD_JPY'
  ];

  const handleIndicatorToggle = (indicator: string) => {
    setSelectedIndicators(prev => 
      prev.includes(indicator) 
        ? prev.filter(i => i !== indicator)
        : [...prev, indicator]
    );
  };

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Header */}
      <div className="border-b border-border-primary bg-bg-secondary/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl tracking-tight font-semibold text-text-primary">
              Advanced Quant Indicators
            </h1>
            <div className="hidden sm:flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-brand-teal-light rounded-full animate-pulse shadow-glow"></div>
                <span className="text-xs font-bold tracking-widest uppercase text-text-secondary">Neural Stream Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Symbol Selection */}
          <GlassCard variant="elevated" className="p-6">
            <h3 className="text-sm font-bold uppercase tracking-widest text-brand-teal mb-6">Target Parameters</h3>
            <div className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="symbol" className="text-xs font-bold uppercase tracking-widest text-text-secondary pl-1">
                  Custom Identifier
                </label>
                <div className="relative group">
                  <input
                    type="text"
                    id="symbol"
                    value={selectedSymbol}
                    onChange={(e) => setSelectedSymbol(e.target.value.toUpperCase())}
                    className="w-full bg-bg-secondary border border-border-primary text-text-primary rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-teal/50 transition-all font-mono"
                    placeholder="Enter ticker..."
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-teal-light opacity-50 group-hover:opacity-100 transition-opacity">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <label className="text-xs font-bold uppercase tracking-widest text-text-secondary pl-1">
                  High-Liquidity Presets
                </label>
                <div className="flex flex-wrap gap-2">
                  {popularSymbols.map(symbol => (
                    <button
                      key={symbol}
                      onClick={() => setSelectedSymbol(symbol)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                        selectedSymbol === symbol
                          ? 'bg-brand-teal/20 border-brand-teal text-brand-teal-light shadow-glow'
                          : 'bg-bg-secondary border-border-primary text-text-secondary hover:border-brand-teal/30 hover:text-text-primary'
                      }`}
                    >
                      {symbol}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>

          {/* Indicator Selection */}
          <GlassCard variant="elevated" className="p-6">
            <h3 className="text-sm font-bold uppercase tracking-widest text-brand-plum-light mb-6">Heuristic Matrix</h3>
            <div className="space-y-6">
              <div className="flex flex-wrap gap-2">
                {availableIndicators.map(indicator => (
                  <button
                    key={indicator}
                    onClick={() => handleIndicatorToggle(indicator)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                      selectedIndicators.includes(indicator)
                        ? 'bg-brand-plum/20 border-brand-plum text-brand-plum-light shadow-[0_0_15px_rgba(217,70,239,0.25)]'
                        : 'bg-bg-secondary border-border-primary text-text-secondary hover:border-brand-plum/30 hover:text-text-primary'
                    }`}
                  >
                    {indicator}
                  </button>
                ))}
              </div>
              
              <div className="flex gap-4 pt-2">
                <GlassButton
                  onClick={() => setSelectedIndicators(availableIndicators)}
                  variant="secondary"
                  size="sm"
                  className="flex-1"
                >
                  Enable All
                </GlassButton>
                <GlassButton
                  onClick={() => setSelectedIndicators([])}
                  variant="ghost"
                  size="sm"
                  className="flex-1 text-brand-plum-light hover:bg-brand-plum/10"
                >
                  Clear Matrix
                </GlassButton>
              </div>
            </div>
          </GlassCard>
        </div>

        {/* Advanced Indicators Chart */}
        <AnimatePresence mode="wait">
          {selectedSymbol && selectedIndicators.length > 0 ? (
            <motion.div
              key={`${selectedSymbol}-${selectedIndicators.length}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <GlassCard variant="glow" className="p-0 overflow-hidden border-border-primary">
                <AdvancedIndicatorsChart 
                  symbol={selectedSymbol}
                  indicators={selectedIndicators}
                  height={typeof window !== 'undefined' && window.innerWidth < 768 ? 400 : 600}
                />
              </GlassCard>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <GlassCard className="p-20 text-center flex flex-col items-center justify-center border-dashed">
                  <div className="w-16 h-16 rounded-full bg-bg-secondary border border-border-primary flex items-center justify-center mb-6 text-text-tertiary">
                    <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-text-primary mb-2">Awaiting Parameters</h3>
                  <p className="text-text-secondary max-w-xs mx-auto font-light">Supply an asset identifier and select heuristics to begin neural mapping.</p>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <GlassCard className="p-6 border-l-4 border-l-brand-teal">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-4">Momentum Oscillators</h4>
            <div className="space-y-4 text-xs">
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-brand-teal-light font-bold">RSI:</span> Measures velocity of price movements.</div>
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-brand-teal-light font-bold">MACD:</span> Captures relationship between two EMAs.</div>
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-brand-teal-light font-bold">CCI:</span> Identifies cyclical trend reversals.</div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-brand-plum">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-4">Volatility Heuristics</h4>
            <div className="space-y-4 text-xs">
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-brand-plum-light font-bold">BB:</span> Statistical volatility envelopes.</div>
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-brand-plum-light font-bold">ATR:</span> Measures absolute market volatility.</div>
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-brand-plum-light font-bold">STOCH:</span> Compares close to high-low range.</div>
            </div>
          </GlassCard>

          <GlassCard className="p-6 border-l-4 border-l-text-secondary">
            <h4 className="text-sm font-bold uppercase tracking-widest text-text-primary mb-4">Volume Intelligence</h4>
            <div className="space-y-4 text-xs">
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-white font-bold">VWAP:</span> True benchmark for execution quality.</div>
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-white font-bold">OBV:</span> Accumulation/distribution flow mapping.</div>
              <div className="p-3 bg-bg-secondary/50 rounded-xl border border-border-primary"><span className="text-white font-bold">V-PROFILE:</span> Volume distribution by price node.</div>
            </div>
          </GlassCard>
        </div>

        {/* Performance Footer */}
        <GlassCard variant="glow" className="p-8 border-border-primary">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h4 className="text-lg font-medium text-brand-teal-light mb-4">Engine Topology</h4>
              <p className="text-text-secondary text-sm leading-relaxed font-light">
                Our proprietary calculation engine executes complex mathematical models on-device, ensuring zero-latency data visualization and absolute privacy for your technical strategies.
              </p>
            </div>
            
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-text-primary tracking-tighter">0.02ms</div>
                  <div className="text-[10px] uppercase font-bold text-text-tertiary">LATENCY</div>
                </div>
                <div className="w-px h-10 bg-border-primary"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-text-primary tracking-tighter">12+</div>
                  <div className="text-[10px] uppercase font-bold text-text-tertiary">HEURISTICS</div>
                </div>
                <div className="w-px h-10 bg-border-primary"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-text-primary tracking-tighter">100%</div>
                  <div className="text-[10px] uppercase font-bold text-text-tertiary">UPTIME</div>
                </div>
              </div>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
